import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, ShoppingBag, X, Clock } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/api";

const LINKS = [
    { label: "Shop", to: "/shop", testid: "nav-shop" },
    { label: "Deals", to: "/deals", testid: "nav-deals" },
    { label: "Blog", to: "/blog", testid: "nav-blog" },
    { label: "Delivery", section: "delivery", testid: "nav-delivery" },
    { label: "FAQ", section: "faq", testid: "nav-faq" },
    { label: "Contact", section: "contact", testid: "nav-contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { count, setDrawerOpen } = useCart();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const goSection = (id) => {
        setMenuOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(id), 140);
        } else {
            setTimeout(() => scrollToId(id), 0);
        }
    };

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-leaf/15" : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="container-x flex items-center justify-between h-16 md:h-20">
                    <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
                        <Logo className="h-9 md:h-11" />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-8">
                        {LINKS.map((l) =>
                            l.to ? (
                                <NavLink
                                    key={l.label}
                                    to={l.to}
                                    data-testid={l.testid}
                                    className={({ isActive }) =>
                                        `eyebrow transition-colors hover:text-leaf-bright ${isActive ? "text-leaf-bright" : "text-bone/80"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ) : (
                                <button
                                    key={l.label}
                                    data-testid={l.testid}
                                    onClick={() => goSection(l.section)}
                                    className="eyebrow text-bone/80 hover:text-leaf-bright transition-colors"
                                >
                                    {l.label}
                                </button>
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-4">
                        <a
                            href="tel:+12027134325"
                            data-testid="header-phone-link"
                            className="hidden md:flex items-center gap-2 text-sm text-bone/80 hover:text-leaf-bright transition-colors"
                        >
                            <Phone size={16} />
                            <span className="font-mono text-xs tracking-wider">(202) 713-4325</span>
                        </a>
                        <button
                            data-testid="cart-button"
                            onClick={() => setDrawerOpen(true)}
                            className="relative flex items-center gap-2 rounded-full bg-leaf px-4 py-2.5 text-ink font-bold text-sm hover:bg-leaf-bright transition-colors"
                        >
                            <ShoppingBag size={17} strokeWidth={2.4} />
                            <span className="hidden sm:inline">Bag</span>
                            <AnimatePresence mode="popLayout">
                                <motion.span
                                    key={count}
                                    initial={{ scale: 0.4, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.4, opacity: 0 }}
                                    data-testid="cart-count-badge"
                                    className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[11px] font-bold text-ink"
                                >
                                    {count}
                                </motion.span>
                            </AnimatePresence>
                        </button>
                        <button
                            data-testid="mobile-menu-button"
                            onClick={() => setMenuOpen(true)}
                            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-leaf/25 text-bone"
                            aria-label="Open menu"
                        >
                            <Menu size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] bg-ink/97 backdrop-blur-2xl flex flex-col"
                        data-testid="mobile-menu"
                    >
                        <div className="container-x flex items-center justify-between h-16">
                            <Logo className="h-9" />
                            <button
                                data-testid="mobile-menu-close"
                                onClick={() => setMenuOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-leaf/25 text-bone"
                                aria-label="Close menu"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center gap-2 container-x">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.label}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {l.to ? (
                                        <Link
                                            to={l.to}
                                            data-testid={`${l.testid}-mobile`}
                                            className="font-display text-5xl font-extrabold text-bone hover:text-leaf-bright transition-colors"
                                        >
                                            {l.label}
                                        </Link>
                                    ) : (
                                        <button
                                            data-testid={`${l.testid}-mobile`}
                                            onClick={() => goSection(l.section)}
                                            className="font-display text-5xl font-extrabold text-bone hover:text-leaf-bright transition-colors text-left"
                                        >
                                            {l.label}
                                        </button>
                                    )}
                                </motion.div>
                            ))}
                        </nav>
                        <div className="container-x pb-10 flex items-center gap-3 text-sage">
                            <Clock size={14} />
                            <span className="font-mono text-xs tracking-widest uppercase">Open 24 Hours · Washington DC — 20007, 20011, 20009</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
